import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNotifications } from '../api/useNotifications';
import {
  NotificationEvent,
  SOCIAL_NOTIFICATION_TYPES,
  isSocialNotification,
} from '../types/notifications.types';
import { useNotificationsSocket } from './useNotificationsSocket';

/**
 * localStorage key holding the ISO timestamp of the most recent social
 * notification the operator has "seen" (i.e. opened the bell after it
 * arrived). Read state is LOCAL-ONLY — there is no backend mark-as-read
 * for the bell. Anything with `occurredAt` strictly newer than this is
 * counted as unread.
 */
export const SOCIAL_LAST_SEEN_KEY = 'predictions:social-notifications:lastSeenAt';

/** Cap on the merged list and on the live buffer behind it. */
const MAX_ITEMS = 50;

function readLastSeen(): string | null {
  try {
    return window.localStorage.getItem(SOCIAL_LAST_SEEN_KEY);
  } catch {
    return null;
  }
}

function writeLastSeen(iso: string): void {
  try {
    window.localStorage.setItem(SOCIAL_LAST_SEEN_KEY, iso);
  } catch {
    /* localStorage unavailable (private mode / SSR) — degrade silently */
  }
}

function toMillis(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Descending sort by (occurredAt, seq): newer first; for equal
 * `occurredAt`, higher `seq` first, with null/undefined `seq` sorting
 * AFTER numbers (so a seq-bearing event wins the tiebreak over a
 * seq-less one at the same instant).
 */
function compareDesc(a: NotificationEvent, b: NotificationEvent): number {
  const ta = toMillis(a.occurredAt);
  const tb = toMillis(b.occurredAt);
  if (ta !== tb) return tb - ta;
  const sa = a.seq;
  const sb = b.seq;
  const aHas = typeof sa === 'number';
  const bHas = typeof sb === 'number';
  if (aHas && bHas) return (sb as number) - (sa as number);
  if (aHas) return -1; // a (has seq) before b (no seq)
  if (bHas) return 1; // b (has seq) before a (no seq)
  return 0;
}

function isSocialEvent(event: NotificationEvent): boolean {
  return isSocialNotification(event.type);
}

/**
 * The bell's live buffer after a socket batch: its social frames join it,
 * deduped by id (an earlier copy wins), newest `occurredAt` first, capped to
 * the `MAX_ITEMS` newest. `prev` itself when the batch adds nothing.
 */
export function appendSocialLive(
  prev: NotificationEvent[],
  batch: readonly NotificationEvent[],
): NotificationEvent[] {
  const ids = new Set(prev.map((e) => e.id));
  const fresh: NotificationEvent[] = [];
  for (const event of batch) {
    if (!isSocialNotification(event.type) || ids.has(event.id)) continue;
    ids.add(event.id);
    fresh.push(event);
  }
  if (fresh.length === 0) return prev;
  return [...fresh, ...prev].sort(compareDesc).slice(0, MAX_ITEMS);
}

/**
 * Aggregates ONLY the "social" notification types for the sidebar bell:
 * REST history (first page) merged with a local live-buffer fed by the
 * shared notifications WebSocket, deduped by id, capped to the 50 most
 * recent, with a LOCAL-ONLY unread count tracked via localStorage.
 */
export function useSocialNotifications() {
  const socialFilter = useMemo(
    () => ({ types: [...SOCIAL_NOTIFICATION_TYPES] }),
    [],
  );

  const { data, refetch } = useNotifications(socialFilter);

  const restItems = useMemo<NotificationEvent[]>(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  );

  const { queueVersion, drain, reconnectCount, isConnected } =
    useNotificationsSocket(isSocialEvent);

  const [liveBuffer, setLiveBuffer] = useState<NotificationEvent[]>([]);

  // The socket queue is per hook instance and only a drain empties it, and
  // the bell is mounted on every predictions page.
  useEffect(() => {
    if (queueVersion === 0) return;
    const batch = drain().map(({ event }) => event);
    setLiveBuffer((prev) => appendSocialLive(prev, batch));
  }, [queueVersion, drain]);

  // On a genuine WS reconnect (skip the initial mount), refetch REST
  // history to close the pub/sub gap and clear the now-stale live buffer.
  const prevReconnect = useRef(reconnectCount);
  useEffect(() => {
    if (reconnectCount === prevReconnect.current) return;
    prevReconnect.current = reconnectCount;
    setLiveBuffer([]);
    void refetch();
  }, [reconnectCount, refetch]);

  // Merge live + REST, dedupe by id (live wins), sort desc, cap to 50.
  const items = useMemo<NotificationEvent[]>(() => {
    const byId = new Map<string, NotificationEvent>();
    for (const item of restItems) byId.set(item.id, item);
    for (const item of liveBuffer) byId.set(item.id, item); // live overrides
    return Array.from(byId.values()).sort(compareDesc).slice(0, MAX_ITEMS);
  }, [restItems, liveBuffer]);

  const [lastSeenAt, setLastSeenAt] = useState<string | null>(() =>
    readLastSeen(),
  );

  // Seed `lastSeenAt` to the newest item the FIRST time data arrives when
  // there is no stored value — so the historical backlog doesn't all show
  // as "unread" on first ever load; only genuinely new arrivals increment.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (items.length === 0) return;
    if (readLastSeen() != null) {
      seededRef.current = true;
      return;
    }
    const newest = items[0].occurredAt;
    seededRef.current = true;
    setLastSeenAt(newest);
    writeLastSeen(newest);
  }, [items]);

  const unreadCount = useMemo(() => {
    const seen = toMillis(lastSeenAt);
    return items.reduce(
      (acc, item) => (toMillis(item.occurredAt) > seen ? acc + 1 : acc),
      0,
    );
  }, [items, lastSeenAt]);

  const markAllRead = useCallback(() => {
    const newest = items.length > 0 ? items[0].occurredAt : new Date().toISOString();
    setLastSeenAt(newest);
    writeLastSeen(newest);
  }, [items]);

  return { items, unreadCount, markAllRead, isConnected };
}
